/**
 * The survey question editor's local model (plan-030 WAPP-Q; design §41.6, §41.10; glossary §6.45) — pure functions
 * over the questionnaire document's `survey` map (one `wetalk-survey-json-v2` file per language, as JSON).
 *
 * WHY JSON AND NOT A TYPED COPY. The editor draws a question's type, code, wording, options and range, the five replies
 * and the spoken lines; a file holds more (keywords, notes, recognition terms, count words, greetings, helpers). Every
 * edit here copies the whole map and changes only the keys it names, so a save sends back everything it did not draw
 * exactly as it was read. The validator (`surveyQuestionnaireEditOf`, through the check route) is the one judge of the
 * result; nothing here decides whether a questionnaire is valid.
 *
 * STRUCTURE IS SHARED, WORDING IS PER LANGUAGE. Every language asks the same questions (the language contract), so
 * adding, removing and moving a question, its code, a choice question's options and limits, and a number question's
 * range change every language's file at once; a question's wording, its clarification, its option labels and the
 * spoken lines change the shown language's file only.
 *
 * A NEW QUESTION LEAVES THE WORDS WETALK ADDS EMPTY. Its clarification (choice, open, number), a several-picks
 * question's follow-up and the ordinal its position needs are filled by the check route from the language pack and
 * warned `wording_added` (OD-7); the tab adopts the filled file and marks each "check the wording".
 */
import { SURVEY_VARIANT, freeQuestionCodeOf, sameQuestionnaireJson, type SurveyVariant } from "@wetalk/domain";

import type { QuestionnaireFileJson, QuestionnaireQuestionType, QuestionnaireSurveyJson } from "../../../../api/slice/survey-questionnaire.js";

/** The five replies the owner writes (OD-5); `are_you_robot`, `unknown` and `hostile` stay as the file has them. */
export const OWNER_REPLY = ["what_is_this", "are_you_local", "who_runs", "where_phone", "how_long"] as const;
export type OwnerReply = (typeof OWNER_REPLY)[number];

/** The spoken lines the editor draws, each a path into a file. */
export const SPOKEN_LINE = ["opening.intro", "opening.declined", "scale.intro", "scale.suffix", "closing"] as const;
export type SpokenLine = (typeof SPOKEN_LINE)[number];

/** A question's wording keys: the shown language's own. */
export type QuestionWordingKey = "text" | "clarify" | "suffix" | "note" | "unit";

export interface OptionShape {
  readonly id: string;
  readonly label: string;
  readonly keywords: readonly string[];
}

/** One question as the editor reads it, whatever is missing from the JSON (a missing string reads ""). */
export interface QuestionShape {
  readonly id: string;
  readonly code: string;
  readonly type: QuestionnaireQuestionType;
  readonly text: string;
  readonly note: string;
  readonly suffix: string;
  readonly clarify: string;
  readonly option: readonly OptionShape[];
  readonly selectionMin: number;
  readonly selectionMax: number;
  readonly exclusive: readonly string[];
  readonly more: string;
  readonly min: number | null;
  readonly max: number | null;
  readonly integer: boolean;
  readonly unit: string;
}

type Mutable = Record<string, unknown>;

function isRecord(value: unknown): value is Mutable {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberOf(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const QUESTION_TYPE: readonly QuestionnaireQuestionType[] = ["scale", "choice", "open", "number"];

function questionTypeOf(value: unknown): QuestionnaireQuestionType {
  const found = QUESTION_TYPE.find((one) => one === value);
  return found === undefined ? "scale" : found;
}

/** The languages of the map, in the order the read gave them. */
export function languagesOf(survey: QuestionnaireSurveyJson): readonly string[] {
  return Object.keys(survey);
}

/** The groups the files hold (`A`, and `B` for a two-group survey), read from the first language. */
export function groupsOf(survey: QuestionnaireSurveyJson): readonly SurveyVariant[] {
  const first = Object.values(survey)[0];
  const variants = first === undefined ? undefined : first["variants"];
  if (!isRecord(variants)) return [];
  return SURVEY_VARIANT.filter((variant) => isRecord(variants[variant]));
}

function questionRecordsOf(file: QuestionnaireFileJson | undefined, variant: SurveyVariant): readonly unknown[] {
  const variants = file === undefined ? undefined : file["variants"];
  const arm = isRecord(variants) ? variants[variant] : undefined;
  const questions = isRecord(arm) ? arm["questions"] : undefined;
  return Array.isArray(questions) ? questions : [];
}

/** One question record, read leniently. */
export function questionShapeOf(record: unknown): QuestionShape {
  const one = isRecord(record) ? record : {};
  const selection = isRecord(one["selection"]) ? one["selection"] : {};
  const option = Array.isArray(one["options"]) ? one["options"] : [];
  return {
    id: textOf(one["id"]),
    code: textOf(one["code"]),
    type: questionTypeOf(one["type"]),
    text: textOf(one["text"]),
    note: textOf(one["note"]),
    suffix: textOf(one["suffix"]),
    clarify: textOf(one["clarify"]),
    option: option.map((each) => {
      const row = isRecord(each) ? each : {};
      return {
        id: textOf(row["id"]),
        label: textOf(row["label"]),
        keywords: Array.isArray(row["keywords"]) ? row["keywords"].map(textOf) : [],
      };
    }),
    selectionMin: numberOf(selection["min"]) ?? 1,
    selectionMax: numberOf(selection["max"]) ?? 1,
    exclusive: Array.isArray(selection["exclusive"]) ? selection["exclusive"].map(textOf) : [],
    more: textOf(selection["more"]),
    min: numberOf(one["min"]),
    max: numberOf(one["max"]),
    integer: one["integer"] === true,
    unit: textOf(one["unit"]),
  };
}

/** A group's questions in one language. */
export function questionsOf(survey: QuestionnaireSurveyJson, language: string, variant: SurveyVariant): readonly QuestionShape[] {
  return questionRecordsOf(survey[language], variant).map(questionShapeOf);
}

/** A line of a file by its dotted path (`opening.intro`, `objections.how_long.answer`), or "" when absent. */
export function lineOf(file: QuestionnaireFileJson | undefined, path: string): string {
  let at: unknown = file;
  for (const key of path.split(".")) {
    if (!isRecord(at)) return "";
    at = at[key];
  }
  return textOf(at);
}

/** The ordinals a group reads: its own, or the shared `scale.ordinals`; and where they are. */
export function ordinalsOf(file: QuestionnaireFileJson | undefined, variant: SurveyVariant): { readonly ordinal: readonly string[]; readonly path: string } {
  const variants = file === undefined ? undefined : file["variants"];
  const arm = isRecord(variants) ? variants[variant] : undefined;
  if (isRecord(arm) && Array.isArray(arm["ordinals"])) return { ordinal: arm["ordinals"].map(textOf), path: `variants.${variant}.ordinals` };
  const scale = file === undefined ? undefined : file["scale"];
  const shared = isRecord(scale) && Array.isArray(scale["ordinals"]) ? scale["ordinals"].map(textOf) : [];
  return { ordinal: shared, path: "scale.ordinals" };
}

/** Whether two maps hold the same questionnaires (object keys in any order). */
export function sameSurvey(left: QuestionnaireSurveyJson, right: QuestionnaireSurveyJson): boolean {
  return sameQuestionnaireJson(left, right);
}

/**
 * The code a new question takes before the check route proposes one: `Q` and the smallest number no file and no code
 * in the history uses (design §41.6.4).
 */
export function freeCodeOf(survey: QuestionnaireSurveyJson, historyCode: readonly string[]): string {
  const taken: string[] = [...historyCode];
  for (const language of languagesOf(survey)) {
    for (const variant of groupsOf(survey)) for (const one of questionsOf(survey, language, variant)) taken.push(one.id);
  }
  return freeQuestionCodeOf(taken);
}

// ── edits: each copies the whole map and changes only what it names ────────────────────────────────────────

function copyOf(survey: QuestionnaireSurveyJson): Record<string, Mutable> {
  return structuredClone(survey) as Record<string, Mutable>;
}

function armOf(file: Mutable, variant: SurveyVariant): Mutable | null {
  const variants = file["variants"];
  const arm = isRecord(variants) ? variants[variant] : undefined;
  return isRecord(arm) ? arm : null;
}

function questionArrayOf(file: Mutable, variant: SurveyVariant): unknown[] | null {
  const arm = armOf(file, variant);
  const questions = arm === null ? undefined : arm["questions"];
  return Array.isArray(questions) ? questions : null;
}

function questionAt(file: Mutable, variant: SurveyVariant, index: number): Mutable | null {
  const one = questionArrayOf(file, variant)?.[index];
  return isRecord(one) ? one : null;
}

/** Every language's file, changed by `edit`. */
function inEveryLanguage(survey: QuestionnaireSurveyJson, edit: (file: Mutable) => void): QuestionnaireSurveyJson {
  const next = copyOf(survey);
  for (const file of Object.values(next)) edit(file);
  return next;
}

/** One language's file, changed by `edit`. */
function inLanguage(survey: QuestionnaireSurveyJson, language: string, edit: (file: Mutable) => void): QuestionnaireSurveyJson {
  const next = copyOf(survey);
  const file = next[language];
  if (file !== undefined) edit(file);
  return next;
}

/** A new question of `type`, its words left for the check route to fill. */
export function newQuestionRecord(type: QuestionnaireQuestionType, code: string): Readonly<Record<string, unknown>> {
  switch (type) {
    case "scale":
      return { id: code, code, type, text: "" };
    case "choice":
      return {
        id: code,
        code,
        type,
        text: "",
        options: [
          { id: "option_1", label: "", keywords: [] },
          { id: "option_2", label: "", keywords: [] },
        ],
        selection: { min: 1, max: 1, exclusive: [] },
      };
    case "open":
      return { id: code, code, type, text: "" };
    case "number":
      return { id: code, code, type, text: "", integer: true };
  }
}

/** Adds a question of `type` with `code` at `index` of a group, in every language. */
export function withQuestionAdded(
  survey: QuestionnaireSurveyJson,
  variant: SurveyVariant,
  index: number,
  type: QuestionnaireQuestionType,
  code: string,
): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const questions = questionArrayOf(file, variant);
    if (questions !== null) questions.splice(Math.max(0, Math.min(index, questions.length)), 0, structuredClone(newQuestionRecord(type, code)));
  });
}

/** Removes the question at `index` of a group, in every language. Its code is never freed (design §41.6.4). */
export function withQuestionRemoved(survey: QuestionnaireSurveyJson, variant: SurveyVariant, index: number): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const questions = questionArrayOf(file, variant);
    if (questions !== null && index >= 0 && index < questions.length) questions.splice(index, 1);
  });
}

/** Moves the question at `index` one place up (`-1`) or down (`+1`), in every language. */
export function withQuestionMoved(survey: QuestionnaireSurveyJson, variant: SurveyVariant, index: number, step: -1 | 1): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const questions = questionArrayOf(file, variant);
    const target = index + step;
    if (questions === null || index < 0 || index >= questions.length || target < 0 || target >= questions.length) return;
    const [moved] = questions.splice(index, 1);
    questions.splice(target, 0, moved);
  });
}

/** A new question's code (its `id`, and the instrument `code` with it), in every language. */
export function withQuestionCode(survey: QuestionnaireSurveyJson, variant: SurveyVariant, index: number, code: string): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null) return;
    question["id"] = code;
    question["code"] = code;
  });
}

/**
 * One wording key of a question in one language. An emptied optional key (`suffix`, `note`, `unit`) is removed, as a
 * file without it says; `text` and `clarify` stay as typed, so an emptied one is a problem the check names.
 */
export function withQuestionWording(
  survey: QuestionnaireSurveyJson,
  language: string,
  variant: SurveyVariant,
  index: number,
  key: QuestionWordingKey,
  value: string,
): QuestionnaireSurveyJson {
  return inLanguage(survey, language, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null) return;
    if ((key === "suffix" || key === "note" || key === "unit") && value.trim() === "") delete question[key];
    else question[key] = value;
  });
}

/** A choice question's limits (how many may be picked, and which picks stand alone), in every language. */
export function withSelection(
  survey: QuestionnaireSurveyJson,
  variant: SurveyVariant,
  index: number,
  change: { readonly min?: number; readonly max?: number; readonly exclusive?: readonly string[] },
): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null) return;
    const selection: Mutable = isRecord(question["selection"]) ? question["selection"] : { min: 1, max: 1, exclusive: [] };
    if (change.min !== undefined) selection["min"] = change.min;
    if (change.max !== undefined) selection["max"] = change.max;
    if (change.exclusive !== undefined) selection["exclusive"] = [...change.exclusive];
    question["selection"] = selection;
  });
}

/** A several-picks question's follow-up in one language; emptied, it is removed (the check names it if needed). */
export function withSelectionMore(survey: QuestionnaireSurveyJson, language: string, variant: SurveyVariant, index: number, value: string): QuestionnaireSurveyJson {
  return inLanguage(survey, language, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null || !isRecord(question["selection"])) return;
    if (value.trim() === "") delete question["selection"]["more"];
    else question["selection"]["more"] = value;
  });
}

/** A number question's range and whether it takes whole numbers only, in every language. `null` removes a bound. */
export function withNumberRange(
  survey: QuestionnaireSurveyJson,
  variant: SurveyVariant,
  index: number,
  change: { readonly min?: number | null; readonly max?: number | null; readonly integer?: boolean },
): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null) return;
    for (const key of ["min", "max"] as const) {
      const value = change[key];
      if (value === undefined) continue;
      if (value === null) delete question[key];
      else question[key] = value;
    }
    if (change.integer !== undefined) question["integer"] = change.integer;
  });
}

/** An option id the question does not use yet: `option_<n>`. */
function freeOptionIdOf(option: readonly unknown[]): string {
  const used = new Set(option.map((one) => (isRecord(one) ? textOf(one["id"]) : "")));
  for (let number = 1; ; number += 1) {
    const id = `option_${String(number)}`;
    if (!used.has(id)) return id;
  }
}

/** Adds an empty option at the end of a choice question, in every language (one id for all). */
export function withOptionAdded(survey: QuestionnaireSurveyJson, variant: SurveyVariant, index: number): QuestionnaireSurveyJson {
  const first = Object.values(survey)[0];
  const record = first === undefined ? undefined : questionRecordsOf(first, variant)[index];
  const id = freeOptionIdOf(isRecord(record) && Array.isArray(record["options"]) ? record["options"] : []);
  return inEveryLanguage(survey, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null) return;
    const option = Array.isArray(question["options"]) ? question["options"] : [];
    option.push({ id, label: "", keywords: [] });
    question["options"] = option;
  });
}

/** Removes an option, in every language; a stand-alone pick naming it goes too. */
export function withOptionRemoved(survey: QuestionnaireSurveyJson, variant: SurveyVariant, index: number, optionIndex: number): QuestionnaireSurveyJson {
  return inEveryLanguage(survey, (file) => {
    const question = questionAt(file, variant, index);
    if (question === null || !Array.isArray(question["options"])) return;
    const [removed] = question["options"].splice(optionIndex, 1);
    const removedId = isRecord(removed) ? textOf(removed["id"]) : "";
    const selection = question["selection"];
    if (isRecord(selection) && Array.isArray(selection["exclusive"])) {
      selection["exclusive"] = selection["exclusive"].filter((one) => one !== removedId);
    }
  });
}

/**
 * An option's label in one language. Its keywords — the words that count as this answer — follow the label while they
 * are empty or still just the label, so a new option is heard by its own words without a second field to fill.
 */
export function withOptionLabel(
  survey: QuestionnaireSurveyJson,
  language: string,
  variant: SurveyVariant,
  index: number,
  optionIndex: number,
  label: string,
): QuestionnaireSurveyJson {
  return inLanguage(survey, language, (file) => {
    const question = questionAt(file, variant, index);
    const option = question !== null && Array.isArray(question["options"]) ? question["options"][optionIndex] : undefined;
    if (!isRecord(option)) return;
    const before = textOf(option["label"]);
    const keywords = Array.isArray(option["keywords"]) ? option["keywords"].map(textOf) : [];
    option["label"] = label;
    if (keywords.length === 0 || (keywords.length === 1 && keywords[0] === before)) option["keywords"] = label.trim() === "" ? [] : [label];
  });
}

/** An option's keywords in one language, from a comma-separated line. */
export function withOptionKeywords(
  survey: QuestionnaireSurveyJson,
  language: string,
  variant: SurveyVariant,
  index: number,
  optionIndex: number,
  line: string,
): QuestionnaireSurveyJson {
  return inLanguage(survey, language, (file) => {
    const question = questionAt(file, variant, index);
    const option = question !== null && Array.isArray(question["options"]) ? question["options"][optionIndex] : undefined;
    if (!isRecord(option)) return;
    option["keywords"] = line.split(",").map((one) => one.trim()).filter((one) => one !== "");
  });
}

/** A line of one language's file by its dotted path (`opening.intro`, `objections.how_long.answer`). */
export function withLine(survey: QuestionnaireSurveyJson, language: string, path: string, value: string): QuestionnaireSurveyJson {
  return inLanguage(survey, language, (file) => {
    const key = path.split(".");
    let at: Mutable = file;
    for (const one of key.slice(0, -1)) {
      const child = at[one];
      if (!isRecord(child)) return;
      at = child;
    }
    const last = key[key.length - 1];
    if (last !== undefined) at[last] = value;
  });
}

/** A group's ordinals in one language, from one line each (blank lines dropped), where the group reads them. */
export function withOrdinals(survey: QuestionnaireSurveyJson, language: string, variant: SurveyVariant, lines: string): QuestionnaireSurveyJson {
  const ordinal = lines.split("\n").map((one) => one.trim()).filter((one) => one !== "");
  return inLanguage(survey, language, (file) => {
    const arm = armOf(file, variant);
    if (arm !== null && Array.isArray(arm["ordinals"])) {
      arm["ordinals"] = ordinal;
      return;
    }
    if (isRecord(file["scale"])) file["scale"]["ordinals"] = ordinal;
  });
}

// ── where a problem or a warning is ────────────────────────────────────────────────────────────────────────

/** A note's place: a question (by its code at the time it was checked) and field, or a line of a file. */
export interface NoteLocation {
  readonly language: string | null;
  readonly variant: SurveyVariant | null;
  /** The question's code in the checked map, so a note follows its question when the cards move. */
  readonly questionId: string | null;
  /** The rest of the path: `text`, `options.1.label`, `selection`; or the file's own (`scale.ordinals`). */
  readonly field: string;
}

/**
 * Where `path` (`survey.el.variants.A.questions.3.options`) is in `checked` (the map the check was made on). A path the
 * editor draws nothing for reads with its whole rest as `field`.
 */
export function locationOf(path: string, checked: QuestionnaireSurveyJson): NoteLocation {
  const part = path.split(".");
  if (part[0] !== "survey" || part.length < 2) return { language: null, variant: null, questionId: null, field: path };
  const language = part[1] ?? null;
  const rest = part.slice(2);
  const variant = SURVEY_VARIANT.find((one) => one === rest[1]);
  if (rest[0] === "variants" && variant !== undefined && rest[2] === "questions" && rest[3] !== undefined && /^\d+$/.test(rest[3])) {
    const file = language === null ? undefined : checked[language];
    const record = questionRecordsOf(file, variant)[Number(rest[3])];
    return {
      language,
      variant,
      questionId: isRecord(record) ? textOf(record["id"]) : null,
      field: rest.slice(4).join("."),
    };
  }
  return { language, variant: variant ?? null, questionId: null, field: rest.join(".") };
}
